import { useState } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { queryClient, apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Building2, Edit2, Save, X, Clock, User } from "lucide-react";
import { format } from "date-fns";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import type { PropertyClosingTeam, ClosingTeamAuditLog } from "@shared/schema";
import { LOAN_TYPES_SOUGHT, type LoanTypeSought } from "@shared/schema";

const LOAN_TYPE_LABELS: Record<LoanTypeSought, string> = {
  conventional: "Conventional",
  fha: "FHA",
  va: "VA",
  usda: "USDA",
  jumbo: "Jumbo",
  dscr: "DSCR",
  hard_money: "Hard Money",
  cash_out_refinance: "Cash-Out Refinance",
  heloc: "HELOC",
  other: "Other",
};

interface ClosingTeamPanelProps {
  propertyId: number;
  readOnly?: boolean;
}

function formatPhone(raw: string): string {
  const digits = raw.replace(/\D/g, "").slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}

const STAGE_LABELS: Record<string, string> = {
  offer_submitted: "Offer Submitted",
  offer_accepted: "Offer Accepted",
  clear_to_close: "Clear to Close",
  closed: "Closed",
  renovations: "Renovation / Acquired",
  active: "Active",
};

const FIELD_LABELS: Record<string, string> = {
  lenderContactName: "Lender Contact Name",
  lenderEmail: "Lender Email",
  lenderPhone: "Lender Phone",
  lenderCompanyName: "Lender Company",
  brokerContactName: "Broker Contact Name",
  brokerEmail: "Broker Email",
  brokerPhone: "Broker Phone",
  brokerCompanyName: "Broker Company",
  loanTypeSought: "Loan Type Sought",
  estimatedValue: "Estimated Property Value",
};

export function ClosingTeamPanel({ propertyId, readOnly = false }: ClosingTeamPanelProps) {
  const { toast } = useToast();
  const [editing, setEditing] = useState(false);
  const [showAudit, setShowAudit] = useState(false);
  type EditableTeamFields = Partial<Omit<PropertyClosingTeam, "loanTypeSought">> & {
    loanTypeSought?: LoanTypeSought | null;
  };
  const [editData, setEditData] = useState<EditableTeamFields>({});
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: team, isLoading } = useQuery<PropertyClosingTeam | null>({
    queryKey: ["/api/app/referrals/closing-team/property", propertyId],
    queryFn: async () => {
      const res = await apiRequest("GET", `/api/app/referrals/closing-team/property/${propertyId}`);
      if (!res.ok) return null;
      return res.json();
    },
  });

  const { data: auditLog = [] } = useQuery<ClosingTeamAuditLog[]>({
    queryKey: ["/api/app/referrals/closing-team/audit", team?.id],
    queryFn: async () => {
      if (!team?.id) return [];
      const res = await apiRequest("GET", `/api/app/referrals/closing-team/${team.id}/audit`);
      return res.json();
    },
    enabled: !!team?.id && showAudit,
  });

  const updateMutation = useMutation({
    mutationFn: async (data: Partial<PropertyClosingTeam>) => {
      return apiRequest("PATCH", `/api/app/referrals/closing-team/${team!.id}`, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/app/referrals/closing-team/property", propertyId] });
      queryClient.invalidateQueries({ queryKey: ["/api/app/referrals/closing-team/audit", team?.id] });
      setEditing(false);
      setEditData({});
      toast({ title: "Closing team updated" });
    },
    onError: () => {
      toast({ title: "Failed to update", variant: "destructive" });
    },
  });

  function startEdit() {
    if (!team) return;
    setEditData({
      lenderContactName: team.lenderContactName || "",
      lenderEmail: team.lenderEmail || "",
      lenderPhone: team.lenderPhone || "",
      lenderCompanyName: team.lenderCompanyName || "",
      brokerContactName: team.brokerContactName || "",
      brokerEmail: team.brokerEmail || "",
      brokerPhone: team.brokerPhone || "",
      brokerCompanyName: team.brokerCompanyName || "",
      loanTypeSought: (team.loanTypeSought as LoanTypeSought | null) ?? null,
      estimatedValue: team.estimatedValue ?? null,
    });
    setEditing(true);
    setErrors({});
  }

  function validate(): boolean {
    const errs: Record<string, string> = {};
    if (!editData.lenderContactName?.trim()) errs.lenderContactName = "Required";
    if (!editData.lenderEmail?.trim()) errs.lenderEmail = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.lenderEmail)) errs.lenderEmail = "Valid email required";
    if (!editData.brokerContactName?.trim()) errs.brokerContactName = "Required";
    if (!editData.brokerEmail?.trim()) errs.brokerEmail = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(editData.brokerEmail)) errs.brokerEmail = "Valid email required";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }

  function handleSave() {
    if (!validate()) return;
    updateMutation.mutate(editData);
  }

  if (isLoading) {
    return (
      <Card data-testid="card-closing-team">
        <CardHeader><CardTitle className="text-base flex items-center gap-2"><Building2 className="h-4 w-4 text-brand" />Closing Team</CardTitle></CardHeader>
        <CardContent><div className="h-24 bg-gray-100 rounded animate-pulse" /></CardContent>
      </Card>
    );
  }

  if (!team) return null;

  return (
    <Card data-testid="card-closing-team">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Building2 className="h-4 w-4 text-brand" />
            Closing Team
            <Badge variant="secondary" className="text-xs font-normal ml-1">
              {STAGE_LABELS[team.capturedAtStage] || team.capturedAtStage}
            </Badge>
          </CardTitle>
          <div className="flex items-center gap-2">
            {team && (
              <button
                className="text-xs text-gray-500 hover:text-gray-700 flex items-center gap-1"
                onClick={() => setShowAudit(p => !p)}
                data-testid="button-toggle-audit"
              >
                <Clock className="h-3.5 w-3.5" />
                History
              </button>
            )}
            {!readOnly && !editing && (
              <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={startEdit} data-testid="button-edit-closing-team">
                <Edit2 className="h-3.5 w-3.5 mr-1" /> Edit
              </Button>
            )}
            {editing && (
              <>
                <Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => { setEditing(false); setEditData({}); }}>
                  <X className="h-3.5 w-3.5 mr-1" /> Cancel
                </Button>
                <Button size="sm" className="h-7 px-2 text-xs" onClick={handleSave} disabled={updateMutation.isPending} data-testid="button-save-closing-team-edit">
                  <Save className="h-3.5 w-3.5 mr-1" /> Save
                </Button>
              </>
            )}
          </div>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="grid grid-cols-2 gap-4">
          {/* Lender */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Building2 className="h-3.5 w-3.5 text-brand" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Lender</span>
              {team.lenderIsRegistered && (
                <Badge className="text-[10px] px-1.5 py-0 bg-blue-100 text-blue-700 border-0">Registered</Badge>
              )}
            </div>
            {editing ? (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-gray-500">Contact Name</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.lenderContactName || ""} onChange={e => setEditData(p => ({ ...p, lenderContactName: e.target.value }))} data-testid="input-edit-lender-name" />
                  {errors.lenderContactName && <p className="text-red-500 text-xs">{errors.lenderContactName}</p>}
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.lenderEmail || ""} onChange={e => setEditData(p => ({ ...p, lenderEmail: e.target.value }))} data-testid="input-edit-lender-email" />
                  {errors.lenderEmail && <p className="text-red-500 text-xs">{errors.lenderEmail}</p>}
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.lenderPhone || ""} onChange={e => setEditData(p => ({ ...p, lenderPhone: formatPhone(e.target.value) }))} data-testid="input-edit-lender-phone" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Company</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.lenderCompanyName || ""} onChange={e => setEditData(p => ({ ...p, lenderCompanyName: e.target.value }))} data-testid="input-edit-lender-company" />
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <p className="font-medium text-gray-900" data-testid="text-lender-name">{team.lenderContactName || "—"}</p>
                {team.lenderCompanyName && <p className="text-gray-500">{team.lenderCompanyName}</p>}
                {team.lenderEmail && <p className="text-gray-500">{team.lenderEmail}</p>}
                {team.lenderPhone && <p className="text-gray-500">{team.lenderPhone}</p>}
              </div>
            )}
          </div>

          {/* Broker */}
          <div className="space-y-3">
            <div className="flex items-center gap-1.5 mb-2">
              <Building2 className="h-3.5 w-3.5 text-green-600" />
              <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Broker / Wholesaler</span>
              {team.brokerIsRegistered && (
                <Badge className="text-[10px] px-1.5 py-0 bg-green-100 text-green-700 border-0">Registered</Badge>
              )}
            </div>
            {editing ? (
              <div className="space-y-2">
                <div>
                  <Label className="text-xs text-gray-500">Contact Name</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.brokerContactName || ""} onChange={e => setEditData(p => ({ ...p, brokerContactName: e.target.value }))} data-testid="input-edit-broker-name" />
                  {errors.brokerContactName && <p className="text-red-500 text-xs">{errors.brokerContactName}</p>}
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Email</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.brokerEmail || ""} onChange={e => setEditData(p => ({ ...p, brokerEmail: e.target.value }))} data-testid="input-edit-broker-email" />
                  {errors.brokerEmail && <p className="text-red-500 text-xs">{errors.brokerEmail}</p>}
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Phone</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.brokerPhone || ""} onChange={e => setEditData(p => ({ ...p, brokerPhone: formatPhone(e.target.value) }))} data-testid="input-edit-broker-phone" />
                </div>
                <div>
                  <Label className="text-xs text-gray-500">Company</Label>
                  <Input className="h-8 text-sm mt-0.5" value={editData.brokerCompanyName || ""} onChange={e => setEditData(p => ({ ...p, brokerCompanyName: e.target.value }))} data-testid="input-edit-broker-company" />
                </div>
              </div>
            ) : (
              <div className="space-y-1 text-sm">
                <p className="font-medium text-gray-900" data-testid="text-broker-name">{team.brokerContactName || "—"}</p>
                {team.brokerCompanyName && <p className="text-gray-500">{team.brokerCompanyName}</p>}
                {team.brokerEmail && <p className="text-gray-500">{team.brokerEmail}</p>}
                {team.brokerPhone && <p className="text-gray-500">{team.brokerPhone}</p>}
              </div>
            )}
          </div>
        </div>

        {/* Loan Details */}
        <div className="mt-4 border-t pt-4">
          <div className="flex items-center gap-1.5 mb-2">
            <Building2 className="h-3.5 w-3.5 text-amber-600" />
            <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Loan Details</span>
          </div>
          {editing ? (
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-gray-500">Loan Type Sought</Label>
                <Select
                  value={editData.loanTypeSought ?? ""}
                  onValueChange={v => setEditData(p => ({ ...p, loanTypeSought: v ? (v as LoanTypeSought) : null }))}
                >
                  <SelectTrigger className="h-8 text-sm mt-0.5" data-testid="trigger-edit-loan-type">
                    <SelectValue placeholder="Select…" />
                  </SelectTrigger>
                  <SelectContent>
                    {LOAN_TYPES_SOUGHT.map(t => (
                      <SelectItem key={t} value={t}>{LOAN_TYPE_LABELS[t]}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-gray-500">Estimated Property Value</Label>
                <Input
                  className="h-8 text-sm mt-0.5"
                  inputMode="decimal"
                  value={editData.estimatedValue ?? ""}
                  onChange={e => {
                    const cleaned = e.target.value.replace(/[^0-9.]/g, "");
                    setEditData(p => ({ ...p, estimatedValue: cleaned || null }));
                  }}
                  placeholder="450000"
                  data-testid="input-edit-estimated-value"
                />
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-xs text-gray-500">Loan Type</span>
                <p className="font-medium text-gray-900" data-testid="text-loan-type">
                  {team.loanTypeSought ? (LOAN_TYPE_LABELS[team.loanTypeSought as LoanTypeSought] || team.loanTypeSought) : "—"}
                </p>
              </div>
              <div>
                <span className="text-xs text-gray-500">Estimated Value</span>
                <p className="font-medium text-gray-900" data-testid="text-estimated-value">
                  {team.estimatedValue ? `$${Number(team.estimatedValue).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Audit Log */}
        {showAudit && auditLog.length > 0 && (
          <div className="mt-4 border-t pt-4">
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-1">
              <Clock className="h-3.5 w-3.5" /> Edit History
            </p>
            <div className="space-y-2">
              {auditLog.map(entry => (
                <div key={entry.id} className="flex items-start gap-2 text-xs text-gray-600" data-testid={`audit-entry-${entry.id}`}>
                  <div className="w-4 h-4 rounded-full bg-gray-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <User className="h-2.5 w-2.5 text-gray-400" />
                  </div>
                  <div>
                    <span className="font-medium">{FIELD_LABELS[entry.fieldChanged] || entry.fieldChanged}</span>
                    {" "}changed from{" "}
                    <span className="bg-red-50 text-red-700 rounded px-1 font-mono">{entry.oldValue || "—"}</span>
                    {" "}to{" "}
                    <span className="bg-green-50 text-green-700 rounded px-1 font-mono">{entry.newValue || "—"}</span>
                    <span className="text-gray-400 ml-2">{entry.changedAt ? format(new Date(entry.changedAt), "MMM d, yyyy h:mm a") : ""}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {showAudit && auditLog.length === 0 && (
          <div className="mt-4 border-t pt-4 text-xs text-gray-500 text-center py-2">No edits recorded yet.</div>
        )}

        <div className="mt-3 text-xs text-gray-400">
          Captured at: {STAGE_LABELS[team.capturedAtStage] || team.capturedAtStage} · {team.capturedAt ? format(new Date(team.capturedAt), "MMM d, yyyy") : ""}
        </div>
      </CardContent>
    </Card>
  );
}
